'use client';
import { Banner, Typography } from '@airbus/components-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import Spinners from '../../shared/components/Spinner';
import GridTable from '../../shared/components/grid/GridTable';
import Layout from '../../shared/components/layout';
import useFetchScreenDetails from '../../shared/utils/useFetchScreenDetails';

import GetAllColumnContextProvider, {
  useColumnContextAPI,
} from '../../shared/context/GetAllColumnContext';
import { useEffect, useState } from 'react';
import NPISteeringSetColumnConfig, {
  getMissingFields,
} from '../../features/npi-steering/components/NPISteeringSetColumnConfig';
import {
  ErledigtAvFormField,
  HtzFormField,
  NPIActions,
  PPVLeaderFormField,
  setNpiSteeringDropdownOptions,
} from '../../features/npi-steering';
import { CommitHandler } from '../../features/npi-steering/commitHandler';
import { t } from '../../i18next';
import { getTbloptStateIdService } from '../../features/npi-steering/services/getTbloptStateIdService';
import { getTbloptBooleanIdService } from '../../features/npi-steering/services/getTbloptBooleanIdService';
import GridWrapper from '../../shared/components/grid/GridWrapper';
import {
  NpiSteeringConfirmBeforeCommit,
  RowsProps,
} from '../../features/npi-steering/services/NpiSteeringConfirmBeforeCommit';
import { extraValidator as npiExtraValidator } from '../../features/npi-steering';
import { PSPInputConfirmBeforeCommit } from '../../features/npi-steering/services/PSPInputConfirmBeforeCommit';

import { AccessData, TableRes } from '../../shared/types';
import { useUserContextApi } from '../../shared/context/UserContext';
import { setAllDropdowns } from '../../shared/services/allDropdownsRegistry';
import { isNeedFieldLocked } from '../../features/npi-steering/services/needFieldLockRule';

export default function NPISteeringPage() {
  const { loading, error, screenTables } = useFetchScreenDetails('npi_steering');
  const parentTable = screenTables?.find(
    (item) => item.mbdDataGrid.dataGridInternalName == 'v_npi_steering',
  );
  const childTable = screenTables?.find(
    (item) => item.mbdDataGrid.dataGridInternalName == 'npi_steering_version_issue',
  );
  const [selectedHTZValue, setSelectedHTZValue] = useState(null);

  if (!screenTables || !parentTable) {
    return null;
  }

  return (
    <>
      {loading ? (
        <Spinners />
      ) : (
        <Layout mainClassName='mainBlock'>
          {error && <Banner variant='warning'>{t('data_not_available')}</Banner>}
          <>
            <GetAllColumnContextProvider>
              <NPISteering tableRes={parentTable} setSelectedHTZValue={setSelectedHTZValue} />
            </GetAllColumnContextProvider>
            {childTable ? (
              <GetAllColumnContextProvider>
                <NPISteeringChild tableRes={childTable} selectedHTZValue={selectedHTZValue} />
              </GetAllColumnContextProvider>
            ) : null}
          </>
        </Layout>
      )}
    </>
  );
}

const NPISteering = (props: { tableRes: TableRes; setSelectedHTZValue: (v: any) => void }) => {
  const { tableRes, setSelectedHTZValue } = props;
  const { updateCustomConfig } = useColumnContextAPI();
  const [booleanIds, setBooleanIds] = useState(null);
  const pspEnabled = useFeatureFlagEnabled('newPspManagement') === true;
  const { userDetails } = useUserContextApi();
  const { allDropdowns } = useColumnContextAPI();

  const renderFormField = [
    {
      attrInternalName: 'htz',
      component: (fieldProps) => <HtzFormField {...fieldProps} tableRes={tableRes} />,
    },
    {
      attrInternalName: 'ppv_leader',
      component: (fieldProps) => <PPVLeaderFormField {...fieldProps} tableRes={tableRes} />,
    },
    {
      attrInternalName: 'erledigt_av',
      component: (fieldProps) => <ErledigtAvFormField {...fieldProps} />,
    },
  ];

  useEffect(() => {
    getBoolIds(setBooleanIds, tableRes);
  }, [tableRes]);
  useEffect(() => {
    setAllDropdowns(allDropdowns);
  }, [allDropdowns]);

  useEffect(() => {
    if (booleanIds) {
      const { boolNoId, boolYesId, doneStateYesId, doneStateNoId } = booleanIds;
      updateCustomConfig({
        CommitHandler,
        SetColumnConfig: (props) =>
          NPISteeringSetColumnConfig({ ...props, tableRes, userDetails, pspEnabled }),
        Actions: NPIActions,
        GetSelectedRowData,
        SetIsCellEditable,
        SetIsDeletionRequestedRow,
        SetDisabledOptionWarning: ({ field }) => field === 'techno_code',
        SetDropdownOptions: setNpiSteeringDropdownOptions,
        renderFormField,
        ScreenSlotProps: { boolNoId, boolYesId, doneStateYesId, doneStateNoId },
        extraValidator: npiExtraValidator,
        ConfirmBeforeCommit: async (
          ...props: [
            rowsProps: RowsProps,
            tableDetails: TableRes,
            {
              accessDetails: AccessData;
              roleIds: number[];
            },
          ]
        ) => {
          const beforeCommitArray: any = [NpiSteeringConfirmBeforeCommit];
          if (pspEnabled) {
            beforeCommitArray.push(PSPInputConfirmBeforeCommit);
          }
          const beforeCommitResults = await Promise.all(
            beforeCommitArray.map((fn) => fn(...props)),
          );

          const { shouldOpenConfirmation, confirmationProps } = beforeCommitResults.reduce(
            (acc, res) => {
              if (res.shouldOpenConfirmation) {
                acc.shouldOpenConfirmation = true;
                acc.confirmationProps.push(res.confirmationProps);
              }
              return acc;
            },
            { shouldOpenConfirmation: false, confirmationProps: [] },
          );
          return {
            shouldOpenConfirmation,
            confirmationProps:
              confirmationProps.length > 1 ? confirmationProps : confirmationProps[0],
          };
        },
      });
    }
  }, [booleanIds]);
  function GetSelectedRowData(selectedRow) {
    setSelectedHTZValue(selectedRow);
  }

  return booleanIds && <GridWrapper tableRes={tableRes} gridBoxHeight='65dvh' />;
};
function SetIsCellEditable(props) {
  const { field, row, access, language, allAccessibleColumns } = props;

  let isEditable = true;
  if (row.npi_id === null) {
    if (
      field === 'lumpsum_value' ||
      field === 'pag_deliverydate_value' ||
      field === 'pag_deliverydate_value_w_fee'
    ) {
      isEditable = false;
      return row.status !== 'D' && access?.edit_row && isEditable;
    }
  }
  
  // Status-based lock override: when need-lock rules apply, force this field to be non-editable for all users.
  if (
    allAccessibleColumns &&
    isNeedFieldLocked({
      field,
      row,
      allAccessibleColumns,
    })
  ) {
    return false;
  }

  if (allAccessibleColumns && field === 'htz') {
    const columnsDetails = allAccessibleColumns.filter(
      (item) => item.attrGroupId === 6 && item.attrMandatory,
    );
    const missingFields = getMissingFields({
      row,
      language,
      columnsDetails,
    });
    if (
      (row.erledigt_me === 2 &&
        row.erledigt_av === 2 &&
        row.indus_id === 1 &&
        !missingFields?.length) ||
      row.status === 'N' ||
      row.status === 'C'
    ) {
      isEditable = true;
    } else isEditable = false;
  }
  const rolePermission = access?.[field]?.accessLevel === 'U';
  return row.status !== 'D' && access?.edit_row && isEditable && rolePermission;
}

function SetIsDeletionRequestedRow({ row }) {
  const indusValue = row?.indus;
  return typeof indusValue === 'string' && indusValue.trim().toLowerCase() === 'deletion requested';
}

const NPISteeringChild = ({
  tableRes,
  selectedHTZValue,
}: {
  tableRes: TableRes;
  selectedHTZValue: any;
}) => {
  const { updateCustomConfig, allAccessibleColumns } = useColumnContextAPI();
  useEffect(() => {
    tableRes.mbdDataGrid.dataGridUniqueIdentifier = '';
    if (selectedHTZValue?.length) {
      const htzList = selectedHTZValue.reduce((resp, item) => {
        if (resp != '') {
          resp += ',';
        }
        resp += `"${item.htz}"`;
        return resp;
      }, '');
      updateCustomConfig({
        AdditionalFilterQuery: `htz:{_in:[${htzList}]}`,
        ScreenSlotProps: {
          customSorting,
        },
      });
    } else {
      updateCustomConfig({
        AdditionalFilterQuery: `htz:{_in:[]}`,
        ScreenSlotProps: {
          customSorting,
        },
      });
    }
  }, [selectedHTZValue]);

  return (
    <>
      {allAccessibleColumns && (
        <Typography variant='h6'>{t('npiSteering.htz_versions')}</Typography>
      )}
      <GridTable tableRes={tableRes} gridBoxHeight='65dvh' />
    </>
  );
};
const customSorting = {
  sortModel: [],
};
async function getBoolIds(setBooleanIds, tableRes) {
  let doneStateNoId = null,
    doneStateYesId = null,
    boolNoId = null,
    boolYesId = null;
  const stateRespNo = await getTbloptStateIdService({ state: 'no', screenGridId: tableRes.id });
  const stateRespYes = await getTbloptStateIdService({ state: 'yes', screenGridId: tableRes.id });
  const boolRespNo = await getTbloptBooleanIdService({ value: 'no', screenGridId: tableRes.id });
  const boolRespYes = await getTbloptBooleanIdService({ value: 'yes', screenGridId: tableRes.id });
  const [noId] = stateRespNo?.npi_tblopt_done_state ?? [];
  const [yesId] = stateRespYes?.npi_tblopt_done_state ?? [];
  const [no] = boolRespNo?.npi_tblopt_boolean_value ?? [];
  const [yes] = boolRespYes?.npi_tblopt_boolean_value ?? [];
  if (noId) {
    doneStateNoId = noId.id;
  }
  if (yesId) {
    doneStateYesId = yesId.id;
  }
  if (no) {
    boolNoId = no.id;
  }
  if (yes) {
    boolYesId = yes.id;
  }
  setBooleanIds({ boolNoId, boolYesId, doneStateYesId, doneStateNoId });
}
